import React, { useEffect, useState } from 'react';
import { type AdminApiClient, ApiError, textOf } from './api-client';
import { parseScopeOptions, type PageScope, type ScopeOptions } from './authorized-scope-client';
import './page-scope-filter.css';

function useListMeta(dataset: string, scope: PageScope) {
  const [meta, setMeta] = useState<{ page: number; totalPages: number; total: number }>();
  useEffect(() => {
    setMeta(undefined);
    const listener = (event: Event) => {
      const detail = (event as CustomEvent).detail as Record<string, unknown>;
      if (
        detail.dataset === dataset &&
        detail.appId === scope.appId &&
        detail.cityId === scope.cityId &&
        detail.page === (scope.page ?? 1) &&
        Number.isSafeInteger(detail.total) &&
        Number.isSafeInteger(detail.totalPages)
      )
        setMeta({
          page: Number(detail.page),
          totalPages: Number(detail.totalPages),
          total: Number(detail.total),
        });
    };
    window.addEventListener('travel:authorized-list', listener);
    return () => window.removeEventListener('travel:authorized-list', listener);
  }, [dataset, scope.appId, scope.cityId, scope.page]);
  return meta;
}
export function PageScopePagination({
  dataset,
  scope,
  onChange,
}: {
  dataset: string;
  scope: PageScope;
  onChange: (scope: PageScope) => void;
}) {
  const meta = useListMeta(dataset, scope);
  if (!meta) return null;
  return (
    <nav className="page-scope-pagination" aria-label="授权数据分页">
      <span>
        共 {meta.total} 条 · 第 {meta.page} / {Math.max(1, meta.totalPages)} 页
      </span>
      <button
        type="button"
        disabled={meta.page <= 1}
        onClick={() => onChange({ ...scope, page: meta.page - 1 })}
      >
        上一页
      </button>
      <button
        type="button"
        disabled={meta.page >= meta.totalPages}
        onClick={() => onChange({ ...scope, page: meta.page + 1 })}
      >
        下一页
      </button>
    </nav>
  );
}
export function PageScopeFilter({
  api,
  dataset,
  scope,
  onChange,
  showPagination = true,
}: {
  api: AdminApiClient;
  dataset: string;
  scope: PageScope;
  onChange: (scope: PageScope) => void;
  showPagination?: boolean;
}) {
  const [options, setOptions] = useState<ScopeOptions>({
    apps: [],
    cities: [],
    cityFilterSupported: false,
  });
  const [status, setStatus] = useState<'loading' | 'ready' | 'permission' | 'error'>('loading');
  const [revision, setRevision] = useState(0);
  const [appSearch, setAppSearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  useEffect(() => {
    let active = true;
    let settled = false;
    setStatus('loading');
    setOptions({ apps: [], cities: [], cityFilterSupported: false });
    const timer = window.setTimeout(() => {
      if (active && !settled) {
        settled = true;
        setStatus('error');
      }
    }, 15000);
    void api.get('/api/admin/business-scope/options?dataset=' + encodeURIComponent(dataset)).then(
      (value) => {
        if (active && !settled) {
          settled = true;
          window.clearTimeout(timer);
          setOptions(parseScopeOptions(value));
          setStatus('ready');
        }
      },
      (error: unknown) => {
        if (active && !settled) {
          settled = true;
          window.clearTimeout(timer);
          setStatus(error instanceof ApiError && error.status === 403 ? 'permission' : 'error');
        }
      },
    );
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [api, dataset, revision]);
  const apps = options.apps.filter((app) =>
    textOf(app, 'name').toLowerCase().includes(appSearch.trim().toLowerCase()),
  );
  const allowedCities = options.cities.filter(
    (city) => !scope.appId || (Array.isArray(city.appIds) && city.appIds.includes(scope.appId)),
  );
  const cities = allowedCities.filter((city) =>
    textOf(city, 'name').toLowerCase().includes(citySearch.trim().toLowerCase()),
  );
  useEffect(() => {
    if (status !== 'ready') return;
    const invalidApp =
      scope.appId && !options.apps.some((app) => textOf(app, 'id') === scope.appId);
    const allowedCities = options.cities.filter(
      (city) => !scope.appId || (Array.isArray(city.appIds) && city.appIds.includes(scope.appId)),
    );
    const invalidCity =
      scope.cityId &&
      (!options.cityFilterSupported ||
        !allowedCities.some((city) => textOf(city, 'id') === scope.cityId));
    if (invalidApp) onChange({ ...scope, appId: '', cityId: '', page: 1 });
    else if (invalidCity) onChange({ ...scope, cityId: '', page: 1 });
  }, [status, options, scope, onChange]);
  const visible = (items: typeof apps, selected: string) => {
    const head = items.slice(0, 8);
    const chosen = items.find((item) => textOf(item, 'id') === selected);
    return chosen && !head.includes(chosen) ? [...head, chosen] : head;
  };
  if (status !== 'ready')
    return (
      <section
        className="page-scope-filter"
        aria-label="本页数据范围筛选"
        aria-busy={status === 'loading'}
      >
        <p className="page-scope-filter__status" role={status === 'loading' ? 'status' : 'alert'}>
          {status === 'loading'
            ? '正在确认本页可访问的小程序和城市…'
            : status === 'permission'
              ? '当前账号尚未获准查看此页面，请在用户详情中检查对应的功能权限。'
              : '暂时无法读取可访问范围，请重试。'}
        </p>
        {status === 'error' && (
          <button
            type="button"
            className="page-scope-filter__retry"
            onClick={() => setRevision((value) => value + 1)}
          >
            重新加载范围
          </button>
        )}
      </section>
    );
  return (
    <section className="page-scope-filter" aria-label="本页数据范围筛选">
      {!options.apps.length && (
        <p className="page-scope-filter__status">
          暂无可访问的小程序范围，请在用户详情中检查数据范围。
        </p>
      )}
      <div className="page-scope-filter__row" role="group" aria-label="本页小程序筛选">
        <span className="page-scope-filter__label">小程序</span>
        {options.apps.length === 1 ? (
          <strong className="page-scope-filter__fixed">
            {textOf(options.apps[0]!, 'name')}（仅此小程序）
          </strong>
        ) : options.apps.length > 1 ? (
          <button
            type="button"
            aria-pressed={!scope.appId}
            onClick={() => onChange({ ...scope, appId: '', cityId: '', page: 1 })}
          >
            全部授权小程序
          </button>
        ) : null}
        {options.apps.length !== 1 &&
          visible(apps, scope.appId).map((app) => (
            <button
              type="button"
              key={textOf(app, 'id')}
              aria-label={'筛选小程序：' + textOf(app, 'name')}
              aria-pressed={scope.appId === textOf(app, 'id')}
              onClick={() => onChange({ ...scope, appId: textOf(app, 'id'), cityId: '', page: 1 })}
            >
              {textOf(app, 'name')}
            </button>
          ))}
        {options.apps.length > 8 && (
          <input
            type="search"
            aria-label="查找授权小程序"
            placeholder="搜索小程序"
            value={appSearch}
            onChange={(event) => setAppSearch(event.target.value)}
          />
        )}
        {appSearch && !apps.length && <small>没有匹配的小程序</small>}
      </div>
      {options.cityFilterSupported && (
        <div className="page-scope-filter__row" role="group" aria-label="本页城市筛选">
          <span className="page-scope-filter__label">城市</span>
          {allowedCities.length === 1 ? (
            <strong className="page-scope-filter__fixed">
              {textOf(allowedCities[0]!, 'name')}（仅此城市）
            </strong>
          ) : (
            <button
              type="button"
              aria-pressed={!scope.cityId}
              onClick={() => onChange({ ...scope, cityId: '', page: 1 })}
            >
              全部授权城市
            </button>
          )}
          {allowedCities.length !== 1 &&
            visible(cities, scope.cityId).map((city) => (
              <button
                type="button"
                key={textOf(city, 'id')}
                aria-label={'筛选城市：' + textOf(city, 'name')}
                aria-pressed={scope.cityId === textOf(city, 'id')}
                onClick={() => onChange({ ...scope, cityId: textOf(city, 'id'), page: 1 })}
              >
                {textOf(city, 'name')}
              </button>
            ))}
          {allowedCities.length > 8 && (
            <input
              type="search"
              aria-label="查找授权城市"
              placeholder="搜索城市"
              value={citySearch}
              onChange={(event) => setCitySearch(event.target.value)}
            />
          )}
          {citySearch && !cities.length && <small>没有匹配的城市</small>}
        </div>
      )}
      {(scope.appId || scope.cityId) && (
        <button
          type="button"
          className="page-scope-filter__clear"
          onClick={() => {
            setAppSearch('');
            setCitySearch('');
            onChange({ ...scope, appId: '', cityId: '', page: 1 });
          }}
        >
          清除范围筛选
        </button>
      )}
      {showPagination && !['products', 'apps', 'cities'].includes(dataset) && (
        <PageScopePagination dataset={dataset} scope={scope} onChange={onChange} />
      )}
    </section>
  );
}
